import React, { createContext, FC, KeyboardEvent, useCallback, useContext, useRef, useState } from "react";
import { Button, Textarea, makeStyles } from "@fluentui/react-components";
import { ChatMessageBoxProps } from "@progress/kendo-react-conversational-ui";
import { ChatBotMessageBoxProps } from "./types";

const MAX_HEIGHT = 200;

const useStyles = makeStyles({
  messageBoxContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  messageBoxRow: {
    display: "flex",
    width: "100%",
    padding: "10px 5px 0",
  },
  messageTextBox: {
    flexGrow: 1,
    backgroundColor: "#F2F8FC",
    border: "1px solid #DADADA",
    minWidth: 0,
    "::after": { display: "none" },
  },
  messageBoxButton: {
    padding: "0px",
  },
  disclaimer: {
    color: "#737373",
    fontSize: "9px",
    fontWeight: 400,
    lineHeight: "12px",
    margin: "5px",
    textAlign: "center",
  },
});

/**
 * Context used to pass dynamic props (disabled, placeholder, onMessageSend) to
 * ChatBotMessageBox without changing the component reference passed to Kendo Chat.
 * This prevents unmount/remount cycles that would wipe the textarea state.
 */
export const ChatBotMessageBoxContext = createContext<ChatBotMessageBoxProps>({
  onMessageSend: () => {},
});

/**
 * Stable wrapper component (module-level reference) passed to Kendo Chat's `messageBox` prop.
 * Reads dynamic values from ChatBotMessageBoxContext, keeping the component identity stable
 * across re-renders so the internal state (typed text) is never lost.
 */
export const ChatBotMessageBoxWrapper: FC<ChatMessageBoxProps> = (props) => {
  const ctx = useContext(ChatBotMessageBoxContext);
  return <ChatBotMessageBox {...props} {...ctx} />;
};

const ChatBotMessageBox: FC<ChatMessageBoxProps & ChatBotMessageBoxProps> = ({
  placeholder,
  disclaimer,
  disabled,
  showMicButton,
  onMessageSend,
}) => {
  const classes = useStyles();
  const [message, setMessage] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resetHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const isSendButtonDisabled = (message: string) => {
    return disabled || !message.trim();
  };

  const isMicButtonDisabled = useCallback(() => {
    return disabled || true; // TODO
  }, [disabled]);

  return (
    <div className={classes.messageBoxContainer}>
      <div className={classes.messageBoxRow}>
        <Textarea
          className={classes.messageTextBox}
          id="chat_bot_text_box"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          resize="none"
          appearance="filled-lighter"
          style={{ padding: "0 2px" }}
          value={message}
          textarea={{ ref: textareaRef, spellCheck: false, style: { overflowY: "auto" } }}
          onChange={(_ev, data) => {
            setMessage(data.value);
            if (textareaRef.current) {
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, MAX_HEIGHT) + "px";
            }
          }}
          onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (message.trim()) {
                onMessageSend(message);
                setMessage("");
                resetHeight();
              }
            }
          }}
        />
        <Button
          className={classes.messageBoxButton}
          id="chat_bot_send_message"
          disabled={isSendButtonDisabled(message)}
          icon={
            <img
              width={40}
              src={isSendButtonDisabled(message) ? "/assets/send-message-disabled.svg" : "/assets/send-message.svg"}
              alt={"send message"}
            />
          }
          appearance={"transparent"}
          onClick={() => {
            onMessageSend(message);
            setMessage("");
            resetHeight();
          }}
        />
        {showMicButton && (
          <Button // TODO implement integration with speech-to-text service
            className={classes.messageBoxButton}
            id="chat_bot_record_message"
            disabled={isMicButtonDisabled()}
            icon={
              <img
                width={37}
                src={isMicButtonDisabled() ? "/assets/microphone-disabled.svg" : "/assets/microphone.svg"}
                alt={"record message"}
              />
            }
            appearance={"transparent"}
            onClick={() => console.log("Recording message")}
          />
        )}
      </div>
      {disclaimer && (
        <div className={classes.disclaimer} data-testid="chat-disclaimer">
          {disclaimer}
        </div>
      )}
    </div>
  );
};

export default ChatBotMessageBox;
